/**
 * THE ENTITY CHAT'S ROUTING RECORD — one per send, posted through studioChat's existing `turnRecord` path,
 * which stores whatever object it is given (spec, "Routing record"). `lane` keeps its glossary meaning — the
 * path inside a chat, here always the parse lane (Q12) — and `chat` names the studio chat. Counts only:
 * never a sentence, an entity name or an id.
 */
import type { EntityParse } from './zs-entity.types';
import type { EntityStep } from './route/entity-route';

export interface EntityTurnRecord {
  v: 1;
  chat: 'entity';
  requestId: string;
  conversationId: string;
  at: number;
  lane: 'parse';
  // Was (2026-10-03, plan 4a — a dropped request's waiting edits are recorded too, never silently lost):
  // step: EntityStep['kind'];
  // Was (2026-10-03, plan 4b — a held action's outcome is recorded too: approved, rejected or abandoned):
  // step: EntityStep['kind'] | 'abandoned';
  step: EntityStep['kind'] | 'abandoned' | 'approved' | 'rejected';
  edits: number;
  declined: number;
  gaps: number;
  waiting: number;
  dropped: number;
  failed: boolean;
  /** Plan 4a final review (K5) — true when the apply came from waiting edits, with no model call. */
  resumed?: boolean;
  /** Plan 4b — a held action's kind, on its hold and on its outcome. */
  action?: 'import' | 'sync' | 'permissions';
}

// Was (2026-10-03, plan 4a final review, K5 — the input and the returned object gained `resumed`):
// export function entityTurnRecord(i: {
//   requestId: string; conversationId: string; at: number; parse: EntityParse; step: EntityStep;
// }): EntityTurnRecord {
//   const s = i.step;
//   return {
//     v: 1,
//     chat: 'entity',
//     requestId: i.requestId,
//     conversationId: i.conversationId,
//     at: i.at,
//     lane: 'parse',
//     step: s.kind,
//     edits: i.parse.edits.length,
//     declined: s.kind === 'apply' ? s.declined.length : 0,
//     gaps: i.parse.gaps.length,
//     waiting: s.kind === 'apply' ? s.waiting.length : 0,
//     dropped: i.parse.dropped,
//     failed: s.kind === 'failed',
//   };
// }
export function entityTurnRecord(i: {
  requestId: string; conversationId: string; at: number; parse: EntityParse; step: EntityStep; resumed?: boolean;
}): EntityTurnRecord {
  const s = i.step;
  return {
    v: 1,
    chat: 'entity',
    requestId: i.requestId,
    conversationId: i.conversationId,
    at: i.at,
    lane: 'parse',
    step: s.kind,
    edits: i.parse.edits.length,
    declined: s.kind === 'apply' ? s.declined.length : 0,
    gaps: i.parse.gaps.length,
    waiting: s.kind === 'apply' ? s.waiting.length : 0,
    dropped: i.parse.dropped,
    failed: s.kind === 'failed',
    ...(i.resumed ? { resumed: true } : {}),
    ...(s.kind === 'hold' ? { action: s.action.kind } : {}),
  };
}

/** A request whose waiting edits were dropped — by a new request, an Undo, or a first entity saved as local (Q16). */
export function abandonedTurnRecord(i: { requestId: string; conversationId: string; at: number; waiting: number }): EntityTurnRecord {
  return {
    v: 1, chat: 'entity', requestId: i.requestId, conversationId: i.conversationId, at: i.at, lane: 'parse',
    step: 'abandoned', edits: 0, declined: 0, gaps: 0, waiting: i.waiting, dropped: 0, failed: false,
  };
}

/** Plan 4b — a held action's outcome: approved (failed = the page didn't finish it), rejected, or abandoned (a new request, or the page closing). */
export function heldOutcomeRecord(i: {
  requestId: string; conversationId: string; at: number;
  outcome: 'approved' | 'rejected' | 'abandoned'; action: 'import' | 'sync' | 'permissions'; failed: boolean;
}): EntityTurnRecord {
  return {
    v: 1, chat: 'entity', requestId: i.requestId, conversationId: i.conversationId, at: i.at, lane: 'parse',
    step: i.outcome, edits: 0, declined: 0, gaps: 0, waiting: 0, dropped: 0, failed: i.failed, action: i.action,
  };
}
